// components/Survey/SurveyResultsPage.jsx — survey reporting: overview & insights, per-question analysis, responses, participation
import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Checkbox, Text, Tooltip as FTooltip } from "@fluentui/react-components";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar,
} from "recharts";
import {
  ArrowLeft24Regular, Share24Regular, Edit24Regular, ArrowDownload24Regular, People24Regular,
  CheckmarkCircle24Filled, Gauge24Regular, Timer24Regular, Lightbulb24Regular, LockClosed24Regular,
  DataPie24Regular, TextBulletListSquare24Regular, Mail24Regular, Delete24Regular, ArrowRepeatAll24Regular,
  Dismiss24Regular, ChevronLeft20Regular, ChevronRight20Regular, ArrowTrending24Regular, Search24Regular,
} from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import {
  selectResponses, summarizeQuestion, assignmentStatus, formatDate, formatDateTime, formatDuration,
  relativeTime, respondentLabel, exportSurveyToExcel, formatAnswer, STATUS_META, SCORED_TYPES, isSurveyOpen,
} from "./surveyUtils";
import { C, card, StatusChip, KpiTile, Meter, EmptyState, LoadingBlock, ErrorBanner, useToast, ConfirmDialog, SectionTitle } from "./surveyUI";
import { QuestionResultCard, VIZ } from "./SurveyCharts";
import SurveyForm from "./SurveyForm";
import ShareSurveyDialog from "./ShareSurveyDialog";
import { usePagination, Pagination } from "../common/Pagination";

const TABS = [
  { key: "overview", label: "Overview & insights", Icon: Lightbulb24Regular },
  { key: "questions", label: "Question analysis", Icon: DataPie24Regular },
  { key: "responses", label: "Individual responses", Icon: TextBulletListSquare24Regular },
  { key: "participation", label: "Participation", Icon: People24Regular },
];

function TrendTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: C.ink }}>{formatDate(p.date)}</div>
      <div style={{ color: C.ink2 }}>{p.daily} new · {p.total} total</div>
    </div>
  );
}

function DeptTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, padding: "8px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.1)", fontSize: 12 }}>
      <div style={{ fontWeight: 700, color: C.ink }}>{p.dept}</div>
      <div style={{ color: C.ink2 }}>{p.responded} of {p.invited} responded · {Math.round(p.rate)}%</div>
    </div>
  );
}

// ── Insights ───────────────────────────────────────────────────────────────────
function buildInsights(questions, responses) {
  const scored = [];
  let nps = null;
  questions.forEach((q) => {
    if (SCORED_TYPES.includes(q.type)) {
      const s = summarizeQuestion(q, responses);
      if (s.normalized !== null && s.answered) scored.push({ label: q.title, score: s.normalized, display: q.type === "nps" ? `${s.avg.toFixed(1)}/10` : `${s.avg.toFixed(2)}/${s.max}`, n: s.answered });
      if (q.type === "nps" && s.nps !== null && nps === null) nps = { ...s, title: q.title };
    } else if (q.type === "likert") {
      const s = summarizeQuestion(q, responses);
      s.rows.forEach((r) => { if (r.normalized !== null) scored.push({ label: r.label, score: r.normalized, display: `${r.avg.toFixed(2)}/${s.columns.length}`, n: r.total }); });
    }
  });
  const sorted = [...scored].sort((a, b) => b.score - a.score);
  const overall = scored.length ? scored.reduce((n, s) => n + s.score, 0) / scored.length : null;
  return {
    strengths: sorted.slice(0, 3),
    improve: sorted.length > 3 ? sorted.slice(-3).reverse() : [],
    overall,
    nps,
  };
}

function ScoreRow({ item, tone }) {
  const pct = item.score * 100;
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid #F5F4F2" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, marginBottom: 6 }}>
        <span style={{ color: C.ink, lineHeight: 1.4 }}>{item.label}</span>
        <span style={{ color: C.ink, fontWeight: 700, whiteSpace: "nowrap" }}>{item.display}</span>
      </div>
      <Meter pct={pct} color={tone === "good" ? VIZ.bar : VIZ.neg[1]} />
    </div>
  );
}

// ── Response detail drawer ─────────────────────────────────────────────────────
function ResponseDrawer({ survey, list, index, onIndex, onClose }) {
  const r = list[index];
  if (!r) return null;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1500, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,26,61,0.32)" }} />
      <div style={{ position: "relative", width: "min(720px, 96vw)", height: "100%", background: C.page, boxShadow: "-8px 0 40px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#fff", padding: "16px 20px", borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontSize: 16, fontWeight: 700, color: C.ink, display: "block" }}>{respondentLabel(survey, r, index)}</Text>
            <Text style={{ fontSize: 12, color: C.muted }}>
              {!survey.settings?.anonymous && r.email ? `${r.email} · ` : ""}{r.department || "No department"} · round {r.round} · {formatDateTime(r.submittedAt)} · {formatDuration(r.durationSec)}
            </Text>
          </div>
          <Button appearance="subtle" icon={<ChevronLeft20Regular />} disabled={index === 0} onClick={() => onIndex(index - 1)} aria-label="Previous response" />
          <Text style={{ fontSize: 12, color: C.ink2 }}>{index + 1} / {list.length}</Text>
          <Button appearance="subtle" icon={<ChevronRight20Regular />} disabled={index === list.length - 1} onClick={() => onIndex(index + 1)} aria-label="Next response" />
          <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} aria-label="Close" />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          <SurveyForm survey={survey} answers={r.answers || {}} onChange={() => {}} readOnly />
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function SurveyResultsPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, isDemoMode } = useUserAccess();
  const { getSurveyData, responseByAssignment, loading, error, refresh, sendReminders, revokeAssignments } = useSurveys();
  const { survey, assignments, responses } = getSurveyData(id);

  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams((p) => { const n = new URLSearchParams(p); n.set("tab", t); return n; }, { replace: true });

  const [mode, setMode] = useState("latest");
  const [dept, setDept] = useState("");
  const [drawerIndex, setDrawerIndex] = useState(null);
  const [respSearch, setRespSearch] = useState("");
  const [partFilter, setPartFilter] = useState("all");
  const [partSearch, setPartSearch] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePreselect, setSharePreselect] = useState([]);
  const [busy, setBusy] = useState("");
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [toast, showToast] = useToast();

  const anonymous = !!survey?.settings?.anonymous;
  const questions = useMemo(() => (survey?.questions || []).filter((q) => q.type !== "section"), [survey]);
  const rounds = useMemo(() => [...new Set(responses.map((r) => r.round))].sort((a, b) => a - b), [responses]);
  const departments = useMemo(() => [...new Set([...responses.map((r) => r.department || "Unspecified"), ...assignments.map((a) => a.department || "Unspecified")])].sort(), [responses, assignments]);

  const selected = useMemo(() => selectResponses(responses, { mode, department: dept }), [responses, mode, dept]);
  // Stable numbering for anonymous labels — oldest response is #1
  const order = useMemo(() => {
    const m = {};
    [...selected].reverse().forEach((r, i) => { m[r.id] = i; });
    return m;
  }, [selected]);
  const labelFor = (r) => respondentLabel(survey, r, order[r.id] ?? 0);

  // Participation
  const participation = useMemo(() => {
    const scoped = dept ? assignments.filter((a) => (a.department || "Unspecified") === dept) : assignments;
    const invited = new Set(scoped.map((a) => a.email));
    const respondedSet = new Set(scoped.filter((a) => responseByAssignment[a.id]).map((a) => a.email));
    const pending = scoped.filter((a) => !responseByAssignment[a.id]);
    const byDept = {};
    assignments.forEach((a) => {
      const d = a.department || "Unspecified";
      byDept[d] = byDept[d] || { dept: d, invitedSet: new Set(), respondedSet: new Set() };
      byDept[d].invitedSet.add(a.email);
      if (responseByAssignment[a.id]) byDept[d].respondedSet.add(a.email);
    });
    const deptRows = Object.values(byDept)
      .map((d) => ({ dept: d.dept, invited: d.invitedSet.size, responded: d.respondedSet.size, rate: d.invitedSet.size ? (d.respondedSet.size / d.invitedSet.size) * 100 : 0 }))
      .sort((a, b) => b.rate - a.rate);
    return { invited: invited.size, responded: respondedSet.size, rate: invited.size ? (respondedSet.size / invited.size) * 100 : 0, pending: pending.length, overdue: pending.filter((a) => assignmentStatus(a, null, survey) === "overdue").length, deptRows };
  }, [assignments, responseByAssignment, dept, survey]);

  const trend = useMemo(() => {
    if (!selected.length) return [];
    const daily = {};
    selected.forEach((r) => { const d = r.submittedAt.slice(0, 10); daily[d] = (daily[d] || 0) + 1; });
    const days = Object.keys(daily).sort();
    const out = [];
    let total = 0;
    const cur = new Date(days[0] + "T12:00:00");
    const end = new Date(days[days.length - 1] + "T12:00:00");
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);
      total += daily[key] || 0;
      out.push({ date: key, label: formatDate(key).replace(/ \d{4}$/, ""), daily: daily[key] || 0, total });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [selected]);

  const insights = useMemo(() => buildInsights(questions, selected), [questions, selected]);
  const avgDuration = selected.length ? selected.reduce((n, r) => n + (r.durationSec || 0), 0) / selected.filter((r) => r.durationSec).length || 0 : 0;

  // Responses tab
  const respList = useMemo(() => {
    const q = respSearch.trim().toLowerCase();
    if (!q) return selected;
    return selected.filter((r) => (!anonymous && (r.name.toLowerCase().includes(q) || r.email.includes(q))) || (r.department || "").toLowerCase().includes(q) || questions.some((qq) => formatAnswer(qq, r.answers).toLowerCase().includes(q)));
  }, [selected, respSearch, anonymous, questions]);
  const respPager = usePagination(respList, { defaultPageSize: 25, resetKey: `${respSearch}|${mode}|${dept}` });

  // Participation tab
  const partRows = useMemo(() => {
    const q = partSearch.trim().toLowerCase();
    return assignments
      .map((a) => ({ ...a, response: responseByAssignment[a.id] || null, status: assignmentStatus(a, responseByAssignment[a.id], survey) }))
      .filter((a) => !dept || (a.department || "Unspecified") === dept)
      .filter((a) => partFilter === "all" || a.status === partFilter || (partFilter === "pending" && a.status === "overdue"))
      .filter((a) => !q || a.name.toLowerCase().includes(q) || a.email.includes(q))
      .sort((a, b) => (a.sharedAt < b.sharedAt ? 1 : -1));
  }, [assignments, responseByAssignment, survey, dept, partFilter, partSearch]);
  const partPager = usePagination(partRows, { defaultPageSize: 50, resetKey: `${partFilter}|${partSearch}|${dept}` });
  const pickedPending = partRows.filter((a) => picked.has(a.id) && !a.response);

  if (!isAdmin) return <EmptyState icon={LockClosed24Regular} title="Access restricted" text="Only administrators can view survey results." />;
  if (loading) return <LoadingBlock label="Loading results…" />;
  if (!survey) return <EmptyState icon={DataPie24Regular} title="Survey not found" text="It may have been deleted." action={<Button onClick={() => navigate("/surveys")}>Back to surveys</Button>} />;

  const status = survey.status === "active" && !isSurveyOpen(survey) ? "closed" : survey.status;

  const remind = async (ids) => {
    setBusy("remind");
    try {
      const r = await sendReminders(survey.id, ids);
      showToast(`${r.sent} reminder${r.sent === 1 ? "" : "s"} sent${r.failed.length ? ` · ${r.failed.length} failed` : ""}`, r.failed.length ? "error" : "success");
      setPicked(new Set());
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setBusy("");
    }
  };

  const openShare = (emails = []) => { setSharePreselect(emails); setShareOpen(true); };

  let qn = 0;

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate("/surveys")} aria-label="Back to surveys" />
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Text style={{ fontSize: 22, fontWeight: 800, color: C.navy }}>{survey.title}</Text>
            <StatusChip status={status} />
            {anonymous && <span style={{ fontSize: 11, fontWeight: 600, color: "#5B3DC4", background: "#F1EDFF", padding: "2px 8px", borderRadius: 20 }}>Anonymous</span>}
          </div>
          <Text style={{ fontSize: 12, color: C.muted }}>
            {questions.length} questions · created {formatDate(survey.createdAt)}{survey.createdByName ? ` by ${survey.createdByName}` : ""}
            {survey.settings?.closesAt ? ` · closes ${formatDate(survey.settings.closesAt)}` : ""}
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<Edit24Regular />} onClick={() => navigate(`/surveys/${survey.id}/edit`)}>Edit</Button>
          <Button icon={<ArrowDownload24Regular />} disabled={!selected.length} onClick={() => exportSurveyToExcel(survey, selected, assignments, responseByAssignment)}>Export Excel</Button>
          <Button appearance="primary" icon={<Share24Regular />} disabled={survey.status === "closed"} onClick={() => openShare()}>Share</Button>
        </div>
      </div>

      <ErrorBanner error={error} onRetry={refresh} />

      {/* Tabs + filters */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16, borderBottom: `1px solid ${C.line}` }}>
        <div style={{ display: "flex", gap: 4, flex: 1, flexWrap: "wrap" }}>
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} type="button" onClick={() => setTab(key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", padding: "10px 12px", fontSize: 14, fontWeight: tab === key ? 700 : 500, color: tab === key ? C.brand : C.ink2, borderBottom: `3px solid ${tab === key ? C.brand : "transparent"}`, marginBottom: -1 }}>
              <Icon style={{ fontSize: 18 }} /> {label}
              {key === "responses" && <span style={{ fontSize: 11, background: C.page, borderRadius: 10, padding: "1px 7px", color: C.ink2 }}>{selected.length}</span>}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, paddingBottom: 8 }}>
          {tab !== "participation" && (
            <select value={String(mode)} onChange={(e) => setMode(e.target.value === "latest" || e.target.value === "all" ? e.target.value : Number(e.target.value))} style={selectStyle} aria-label="Responses to include">
              <option value="latest">Latest per person</option>
              <option value="all">All submissions</option>
              {rounds.length > 1 && rounds.map((r) => <option key={r} value={r}>Round {r} only</option>)}
            </select>
          )}
          <select value={dept} onChange={(e) => setDept(e.target.value)} style={selectStyle} aria-label="Department">
            <option value="">All departments</option>
            {departments.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <KpiTile icon={People24Regular} label="Invited" value={participation.invited} sub={`${participation.pending} open invitation${participation.pending === 1 ? "" : "s"}${participation.overdue ? ` · ${participation.overdue} overdue` : ""}`} />
            <KpiTile icon={CheckmarkCircle24Filled} label="Responded" value={participation.responded} sub={`${selected.length} response${selected.length === 1 ? "" : "s"} in view`} color="#0B6A0B" />
            <KpiTile icon={Gauge24Regular} label="Response rate" value={`${Math.round(participation.rate)}%`} color="#5B3DC4">
              <Meter pct={participation.rate} color="#5B3DC4" />
            </KpiTile>
            <KpiTile icon={Timer24Regular} label="Avg. completion time" value={formatDuration(avgDuration)} sub={selected[0] ? `Last response ${relativeTime(selected[0].submittedAt)}` : "No responses yet"} color="#B8430A" />
          </div>

          {selected.length === 0 ? (
            <EmptyState icon={DataPie24Regular} title="No responses yet" text={assignments.length ? "Results will appear here as soon as staff start responding." : "Share this survey with staff to start collecting responses."} action={<Button appearance="primary" icon={<Share24Regular />} onClick={() => openShare()} disabled={survey.status === "closed"}>Share survey</Button>} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 14 }}>
              {/* Key insights */}
              <div style={{ ...card, padding: "18px 20px" }}>
                <SectionTitle>Key insights</SectionTitle>
                <div style={{ display: "flex", gap: 28, flexWrap: "wrap", marginBottom: 10 }}>
                  {insights.overall !== null && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Overall favourability</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: C.ink }}>{Math.round(insights.overall * 100)}%</div>
                      <div style={{ fontSize: 11, color: C.muted }}>Average of all rated items, scaled 0–100</div>
                    </div>
                  )}
                  {insights.nps && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Net Promoter Score</div>
                      <div style={{ fontSize: 30, fontWeight: 800, color: C.ink }}>{insights.nps.nps > 0 ? `+${insights.nps.nps}` : insights.nps.nps}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{insights.nps.segments.promoters} promoters · {insights.nps.segments.detractors} detractors</div>
                    </div>
                  )}
                </div>
                {insights.strengths.length > 0 ? (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, color: VIZ.bar, marginTop: 6 }}>Top-rated</div>
                    {insights.strengths.map((s) => <ScoreRow key={`s-${s.label}`} item={s} tone="good" />)}
                    {insights.improve.length > 0 && (
                      <>
                        <div style={{ fontSize: 12, fontWeight: 700, color: VIZ.neg[2], marginTop: 14 }}>Areas to improve</div>
                        {insights.improve.map((s) => <ScoreRow key={`i-${s.label}`} item={s} tone="bad" />)}
                      </>
                    )}
                  </>
                ) : (
                  <Text style={{ fontSize: 13, color: C.muted }}>Add rating, scale or Likert questions to see strengths and improvement areas.</Text>
                )}
              </div>

              {/* Trend */}
              <div style={{ ...card, padding: "18px 20px" }}>
                <SectionTitle right={<span style={{ fontSize: 12, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}><ArrowTrending24Regular style={{ fontSize: 14 }} /> cumulative</span>}>Responses over time</SectionTitle>
                <div style={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="svTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={VIZ.bar} stopOpacity={0.28} />
                          <stop offset="100%" stopColor={VIZ.bar} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke={VIZ.grid} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: VIZ.axis }} axisLine={{ stroke: VIZ.grid }} tickLine={false} minTickGap={24} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: VIZ.axis }} axisLine={false} tickLine={false} />
                      <Tooltip content={<TrendTooltip />} cursor={{ stroke: VIZ.axis, strokeDasharray: "3 3" }} />
                      <Area type="monotone" dataKey="total" stroke={VIZ.bar} strokeWidth={2} fill="url(#svTrend)" dot={trend.length < 20 ? { r: 4, fill: VIZ.bar, stroke: "#fff", strokeWidth: 2 } : false} activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Department participation */}
              {participation.deptRows.length > 0 && (
                <div style={{ ...card, padding: "18px 20px" }}>
                  <SectionTitle>Response rate by department</SectionTitle>
                  <div style={{ height: Math.max(160, participation.deptRows.length * 34 + 30) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={participation.deptRows} layout="vertical" margin={{ top: 0, right: 40, left: 0, bottom: 0 }} barCategoryGap="22%">
                        <CartesianGrid horizontal={false} stroke={VIZ.grid} />
                        <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: VIZ.axis }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="dept" width={120} tick={{ fontSize: 12, fill: C.ink2 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<DeptTooltip />} cursor={{ fill: "rgba(42,120,214,0.06)" }} />
                        <Bar dataKey="rate" fill={VIZ.bar} radius={[0, 4, 4, 0]} maxBarSize={22} label={{ position: "right", fontSize: 11, fill: C.ink2, formatter: (v) => `${Math.round(v)}%` }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Snapshot of first scored questions */}
              <div style={{ ...card, padding: "18px 20px" }}>
                <SectionTitle right={<Button size="small" appearance="subtle" onClick={() => setTab("questions")}>All questions</Button>}>Question snapshot</SectionTitle>
                {questions.map((q) => {
                  const s = summarizeQuestion(q, selected);
                  let value = `${s.answered} answers`;
                  if (s.kind === "scale") value = q.type === "nps" ? `NPS ${s.nps ?? "—"}` : `${s.avg?.toFixed(2) ?? "—"} / ${s.max}`;
                  else if (s.kind === "choice" && s.top) value = `${s.top.label} (${Math.round(s.top.pct)}%)`;
                  else if (s.kind === "binary") value = `${Math.round(s.rows[0].pct)}% yes`;
                  else if (s.kind === "likert") value = `${s.rows.length} statements`;
                  return (
                    <div key={q.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 0", borderBottom: "1px solid #F5F4F2", fontSize: 13 }}>
                      <span style={{ color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.title}</span>
                      <span style={{ color: C.ink, fontWeight: 600, whiteSpace: "nowrap", maxWidth: "45%", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
                    </div>
                  );
                }).slice(0, 8)}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Questions ── */}
      {tab === "questions" && (
        selected.length === 0
          ? <EmptyState icon={DataPie24Regular} title="No responses in view" text="Change the filters above or wait for staff to respond." />
          : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {survey.questions.map((q) => {
                if (q.type === "section") {
                  return <Text key={q.id} style={{ fontSize: 16, fontWeight: 800, color: C.navy, margin: "10px 2px 0" }}>{q.title}</Text>;
                }
                qn++;
                return <QuestionResultCard key={q.id} question={q} number={qn} summary={summarizeQuestion(q, selected)} anonymous={anonymous} labelFor={labelFor} totalResponses={selected.length} />;
              })}
            </div>
          )
      )}

      {/* ── Responses ── */}
      {tab === "responses" && (
        <div style={{ ...card, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.line}`, display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
              <Search24Regular style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: C.muted }} />
              <input value={respSearch} onChange={(e) => setRespSearch(e.target.value)} placeholder={anonymous ? "Search answers or department" : "Search name, email or answers"} style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px 8px 34px", border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, outline: "none" }} />
            </div>
            <Text style={{ fontSize: 12, color: C.muted, marginLeft: "auto" }}>Click a row to read the full response</Text>
          </div>
          {respList.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>No responses to show.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.soft }}>
                    {["Respondent", "Department", "Round", "Submitted", "Time taken", ...questions.slice(0, 3).map((q) => q.title)].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {respPager.pageItems.map((r) => (
                    <tr key={r.id} onClick={() => setDrawerIndex(respList.indexOf(r))} style={{ cursor: "pointer", borderTop: "1px solid #F5F4F2" }} onMouseEnter={(e) => { e.currentTarget.style.background = "#F7FAFF"; }} onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}>
                      <td style={{ padding: "10px 14px", fontWeight: 600, color: C.ink, whiteSpace: "nowrap" }}>{labelFor(r)}</td>
                      <td style={{ padding: "10px 14px", color: C.ink2 }}>{r.department || "—"}</td>
                      <td style={{ padding: "10px 14px", color: C.ink2 }}>{r.round}</td>
                      <td style={{ padding: "10px 14px", color: C.ink2, whiteSpace: "nowrap" }}>{formatDateTime(r.submittedAt)}</td>
                      <td style={{ padding: "10px 14px", color: C.ink2 }}>{formatDuration(r.durationSec)}</td>
                      {questions.slice(0, 3).map((q) => (
                        <td key={q.id} style={{ padding: "10px 14px", color: C.ink, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{formatAnswer(q, r.answers).replace(/\n/g, "; ") || <span style={{ color: C.muted }}>—</span>}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination {...respPager} accent={C.brand} noun="response" />
        </div>
      )}

      {/* ── Participation ── */}
      {tab === "participation" && (
        <div style={{ ...card, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.line}`, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[["all", "All"], ["pending", "Awaiting"], ["submitted", "Submitted"], ["expired", "Closed"]].map(([k, label]) => {
                const n = assignments.filter((a) => {
                  const st = assignmentStatus(a, responseByAssignment[a.id], survey);
                  return (!dept || (a.department || "Unspecified") === dept) && (k === "all" || st === k || (k === "pending" && st === "overdue"));
                }).length;
                return (
                  <button key={k} type="button" onClick={() => setPartFilter(k)} style={{ border: `1px solid ${partFilter === k ? "#C7DBFF" : C.line}`, background: partFilter === k ? "#F0F6FF" : "#fff", color: partFilter === k ? C.brand : C.ink2, fontWeight: partFilter === k ? 700 : 500, borderRadius: 20, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                    {label} {n}
                  </button>
                );
              })}
            </div>
            <div style={{ position: "relative", width: 240 }}>
              <Search24Regular style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: C.muted }} />
              <input value={partSearch} onChange={(e) => setPartSearch(e.target.value)} placeholder="Search staff" style={{ width: "100%", boxSizing: "border-box", padding: "7px 12px 7px 34px", border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, outline: "none" }} />
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <FTooltip content={isDemoMode ? "Emails are unavailable in demo mode" : "Email a reminder to selected staff who have not responded"} relationship="description">
                <Button size="small" icon={<Mail24Regular />} disabled={!pickedPending.length || !!busy || isDemoMode} onClick={() => remind(pickedPending.map((a) => a.id))}>
                  {busy === "remind" ? "Sending…" : `Remind${pickedPending.length ? ` (${pickedPending.length})` : ""}`}
                </Button>
              </FTooltip>
              <Button size="small" icon={<Delete24Regular />} disabled={!pickedPending.length || !!busy} onClick={() => setRevokeOpen(true)}>Revoke</Button>
              <Button size="small" appearance="primary" icon={<ArrowRepeatAll24Regular />} disabled={survey.status === "closed"} onClick={() => openShare(partRows.filter((a) => picked.has(a.id) && a.response).map((a) => a.email))}>
                Re-share{partRows.some((a) => picked.has(a.id) && a.response) ? " selected" : ""}
              </Button>
            </div>
          </div>
          {anonymous && (
            <div style={{ padding: "8px 16px", background: "#F6F3FF", color: "#5B3DC4", fontSize: 12, borderBottom: `1px solid ${C.line}` }}>
              Anonymous survey — this list shows who has responded, never what they answered.
            </div>
          )}
          {partRows.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>{assignments.length ? "No one matches this filter." : "This survey has not been shared yet."}</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.soft }}>
                    <th style={{ width: 40, padding: "8px 0 8px 14px" }}>
                      <Checkbox
                        checked={partPager.pageItems.length > 0 && partPager.pageItems.every((a) => picked.has(a.id)) ? true : partPager.pageItems.some((a) => picked.has(a.id)) ? "mixed" : false}
                        onChange={(_, d) => setPicked((prev) => { const n = new Set(prev); partPager.pageItems.forEach((a) => (d.checked ? n.add(a.id) : n.delete(a.id))); return n; })}
                        aria-label="Select page"
                      />
                    </th>
                    {["Staff", "Department", "Round", "Shared", "Due", "Status", "Submitted", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "10px 12px", fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {partPager.pageItems.map((a) => (
                    <tr key={a.id} style={{ borderTop: "1px solid #F5F4F2" }}>
                      <td style={{ padding: "8px 0 8px 14px" }}>
                        <Checkbox checked={picked.has(a.id)} onChange={() => setPicked((prev) => { const n = new Set(prev); if (n.has(a.id)) n.delete(a.id); else n.add(a.id); return n; })} aria-label={`Select ${a.name}`} />
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <div style={{ fontWeight: 600, color: C.ink }}>{a.name}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{a.email}</div>
                      </td>
                      <td style={{ padding: "8px 12px", color: C.ink2 }}>{a.department || "—"}</td>
                      <td style={{ padding: "8px 12px", color: C.ink2 }}>{a.round}</td>
                      <td style={{ padding: "8px 12px", color: C.ink2, whiteSpace: "nowrap" }} title={a.sharedByName ? `by ${a.sharedByName}` : ""}>{formatDate(a.sharedAt)}</td>
                      <td style={{ padding: "8px 12px", color: a.status === "overdue" ? STATUS_META.overdue.color : C.ink2, whiteSpace: "nowrap" }}>{a.dueDate ? formatDate(a.dueDate) : "—"}</td>
                      <td style={{ padding: "8px 12px" }}><StatusChip status={a.status} /></td>
                      <td style={{ padding: "8px 12px", color: C.ink2, whiteSpace: "nowrap" }}>{a.response ? formatDateTime(a.response.submittedAt) : "—"}</td>
                      <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                        {a.response && !anonymous && (
                          <Button size="small" appearance="subtle" onClick={() => { setMode("all"); setDept(""); setRespSearch(""); setTab("responses"); setTimeout(() => setDrawerIndex(selectResponses(responses, { mode: "all" }).findIndex((r) => r.id === a.response.id)), 0); }}>View</Button>
                        )}
                        {!a.response && !isDemoMode && <Button size="small" appearance="subtle" icon={<Mail24Regular />} disabled={!!busy} onClick={() => remind([a.id])} aria-label={`Remind ${a.name}`} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination {...partPager} accent={C.brand} noun="invitation" />
        </div>
      )}

      {drawerIndex !== null && drawerIndex >= 0 && (
        <ResponseDrawer survey={survey} list={respList} index={drawerIndex} onIndex={setDrawerIndex} onClose={() => setDrawerIndex(null)} />
      )}

      {shareOpen && <ShareSurveyDialog surveyId={survey.id} preselect={sharePreselect} onClose={() => setShareOpen(false)} onDone={(msg, type) => showToast(msg, type)} />}

      <ConfirmDialog
        open={revokeOpen}
        title="Revoke invitations?"
        danger
        busy={busy === "revoke"}
        confirmLabel={`Revoke ${pickedPending.length}`}
        onCancel={() => setRevokeOpen(false)}
        onConfirm={async () => {
          setBusy("revoke");
          try {
            const n = await revokeAssignments(pickedPending.map((a) => a.id));
            showToast(`${n} invitation${n === 1 ? "" : "s"} revoked`);
            setPicked(new Set());
            setRevokeOpen(false);
          } catch (err) {
            showToast(err.message, "error");
          } finally {
            setBusy("");
          }
        }}
      >
        The selected staff will no longer see this survey. Submitted responses are never affected.
      </ConfirmDialog>

      {toast}
    </div>
  );
}

const selectStyle = { border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13, padding: "6px 10px", color: C.ink, background: "#fff", cursor: "pointer" };
